import { apiClient } from './client';
import { downloadPdfBlob } from '../utils/downloadPdf';
import {
  CleanActivityEvaluation,
  CleanActivityEvaluationPayload,
  CleanActivitySheet,
  CleanActivitySheetPayload,
} from '../types/cleanActivity';
import { SequentialCodeSuggestion } from '../types/sequentialCode';

export async function fetchCleanSheets(keyword?: string): Promise<CleanActivitySheet[]> {
  const { data } = await apiClient.get<CleanActivitySheet[]>('/clean-activities/sheets', {
    params: keyword?.trim() ? { q: keyword.trim() } : undefined,
  });
  return data;
}

export async function fetchNextCleanSheetNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/clean-activities/sheets/suggestions/sheet-no'
  );
  return data;
}

export async function fetchCleanSheet(sheetId: number): Promise<CleanActivitySheet> {
  const { data } = await apiClient.get<CleanActivitySheet>(`/clean-activities/sheets/${sheetId}`);
  return data;
}

export async function createCleanSheet(
  payload: CleanActivitySheetPayload
): Promise<CleanActivitySheet> {
  const { data } = await apiClient.post<CleanActivitySheet>('/clean-activities/sheets', payload);
  return data;
}

export async function updateCleanSheet(
  sheetId: number,
  payload: CleanActivitySheetPayload
): Promise<CleanActivitySheet> {
  const { data } = await apiClient.patch<CleanActivitySheet>(
    `/clean-activities/sheets/${sheetId}`,
    payload
  );
  return data;
}

export async function deleteCleanSheet(sheetId: number): Promise<void> {
  await apiClient.delete(`/clean-activities/sheets/${sheetId}`);
}

export async function approveCleanSheet(sheetId: number): Promise<CleanActivitySheet> {
  const { data } = await apiClient.post<CleanActivitySheet>(
    `/clean-activities/sheets/${sheetId}/approve`
  );
  return data;
}

export async function revokeCleanSheet(sheetId: number): Promise<CleanActivitySheet> {
  const { data } = await apiClient.post<CleanActivitySheet>(
    `/clean-activities/sheets/${sheetId}/revoke`
  );
  return data;
}

export async function fetchCleanEvaluations(
  keyword?: string
): Promise<CleanActivityEvaluation[]> {
  const { data } = await apiClient.get<CleanActivityEvaluation[]>(
    '/clean-activities/evaluations',
    { params: keyword?.trim() ? { q: keyword.trim() } : undefined }
  );
  return data;
}

export async function fetchNextCleanEvaluationNo(): Promise<SequentialCodeSuggestion> {
  const { data } = await apiClient.get<SequentialCodeSuggestion>(
    '/clean-activities/evaluations/suggestions/evaluation-no'
  );
  return data;
}

export async function fetchCleanEvaluation(
  evaluationId: number
): Promise<CleanActivityEvaluation> {
  const { data } = await apiClient.get<CleanActivityEvaluation>(
    `/clean-activities/evaluations/${evaluationId}`
  );
  return data;
}

export async function createCleanEvaluation(
  payload: CleanActivityEvaluationPayload
): Promise<CleanActivityEvaluation> {
  const { data } = await apiClient.post<CleanActivityEvaluation>(
    '/clean-activities/evaluations',
    payload
  );
  return data;
}

export async function updateCleanEvaluation(
  evaluationId: number,
  payload: CleanActivityEvaluationPayload
): Promise<CleanActivityEvaluation> {
  const { data } = await apiClient.patch<CleanActivityEvaluation>(
    `/clean-activities/evaluations/${evaluationId}`,
    payload
  );
  return data;
}

export async function deleteCleanEvaluation(evaluationId: number): Promise<void> {
  await apiClient.delete(`/clean-activities/evaluations/${evaluationId}`);
}

export async function approveCleanEvaluation(
  evaluationId: number
): Promise<CleanActivityEvaluation> {
  const { data } = await apiClient.post<CleanActivityEvaluation>(
    `/clean-activities/evaluations/${evaluationId}/approve`
  );
  return data;
}

export async function revokeCleanEvaluation(
  evaluationId: number
): Promise<CleanActivityEvaluation> {
  const { data } = await apiClient.post<CleanActivityEvaluation>(
    `/clean-activities/evaluations/${evaluationId}/revoke`
  );
  return data;
}


export async function downloadCleanSheetPdf(
  sheetId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/clean-activities/sheets/${sheetId}/pdf`,
    fileName || `청결점검시트_${sheetId}.pdf`
  );
}

export async function downloadCleanEvaluationPdf(
  evaluationId: number,
  fileName?: string
): Promise<void> {
  await downloadPdfBlob(
    `/clean-activities/evaluations/${evaluationId}/pdf`,
    fileName || `청결평가_${evaluationId}.pdf`
  );
}
